import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Navigation, Star } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';

const ADDR_QUERY = encodeURIComponent('158 Liberty Rose Dr, Morrisville, NC 27560');

export const Contact = () => {
  return (
    <section id="contact" data-testid={CONTACT.root} className="py-24 lg:py-32 bg-ivory">
      <div className="container-luxe">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="overline">Visit Us</span>
          <h2 className="font-display text-4xl lg:text-5xl text-charcoal mt-3 leading-tight">
            We’d love to see you <em className="text-maroon not-italic font-medium">in person</em>.
          </h2>
          <div className="mt-5 mx-auto h-px w-16 bg-gold" />
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 space-y-8"
          >
            <div className="flex gap-5">
              <div className="w-12 h-12 shrink-0 border border-gold flex items-center justify-center text-maroon"><MapPin className="w-5 h-5" /></div>
              <div>
                <div className="overline">Address</div>
                <div className="font-display text-xl text-charcoal mt-1">158 Liberty Rose Dr</div>
                <div className="text-charcoal/70">Morrisville, North Carolina 27560</div>
              </div>
            </div>
            <div className="flex gap-5">
              <div className="w-12 h-12 shrink-0 border border-gold flex items-center justify-center text-maroon"><Phone className="w-5 h-5" /></div>
              <div>
                <div className="overline">Phone</div>
                <a href="tel:+19843779835" className="font-display text-xl text-charcoal mt-1 hover:text-maroon transition-colors">984-377-9835</a>
                <div className="text-charcoal/70 text-sm">Tap to call · Mon–Sun</div>
              </div>
            </div>
            <div className="flex gap-5">
              <div className="w-12 h-12 shrink-0 border border-gold flex items-center justify-center text-maroon"><Clock className="w-5 h-5" /></div>
              <div>
                <div className="overline">Business Hours</div>
                <div className="font-display text-xl text-charcoal mt-1">Open Daily · 4:30 PM – 8:30 PM</div>
                <div className="text-charcoal text-sm font-semibold">Please call or text before visiting</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href="tel:+19843779835"
                data-testid={CONTACT.call}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.22em] transition-colors duration-500"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${ADDR_QUERY}`}
                target="_blank" rel="noreferrer"
                data-testid={CONTACT.directions}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-gold text-charcoal hover:bg-gold hover:text-ivory text-xs uppercase tracking-[0.22em] transition-all duration-500"
              >
                <Navigation className="w-4 h-4" /> Get Directions
              </a>
              <a
                href="https://www.google.com/search?q=Trendy+Closet+Indian+Clothing+Reviews"
                target="_blank" rel="noreferrer"
                data-testid="contact-google-reviews"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-maroon text-maroon hover:bg-maroon hover:text-ivory text-xs uppercase tracking-[0.22em] transition-all duration-500"
              >
                <Star className="w-4 h-4 fill-current" /> Google Reviews
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative w-full aspect-[4/3] lg:aspect-[16/11] overflow-hidden border border-brandborder shadow-luxe">
              <iframe
                data-testid={CONTACT.map}
                title="Trendy Closet location"
                src={`https://www.google.com/maps?q=${ADDR_QUERY}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
